import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Grid,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Table, TableBody, TableCell, TableHead, TableRow, Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ContentCopy as DuplicateIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Calculate as CalculateIcon,
  Description as DocumentIcon,
  TableChart as ExcelIcon,
  Edit as EditIcon,
  PersonAdd as AddPersonIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import {
  STATUS_COLORS, formatPeriod, formatMoney,
  isValidQuotationPeriod, adjustEndDate,
  EDITABLE_STATUSES, contractPeople, quotationEditBlock,
  MINIMUM_WAGE_CODE, minimumWageMessage, FORMULA_PROVISIONS,
} from './quotationUtils';
import { SimulationView } from './SimulationBreakdown';
import ErpDateField from '../ErpDateField';
import { formatDate } from '../formatDate';

// Errores de configuración del cálculo (409): faltan parámetros del período
const SETUP_CODES = ['ENGINE_NOT_READY', 'PARAMETER_SET_MISSING', 'PARAMETER_SET_INCOMPLETE'];

const Row = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <strong>{label}:</strong> {value ?? '—'}
  </Typography>
);

/**
 * Fecha de ingreso + "período hasta" (por defecto fin de ese mes; desde el
 * 2026-10-31 puede caer en otro mes, sin enlazarse al término de la madre).
 * Lo usan "Agregar persona" y "Cambiar fecha de ingreso" de una subcotización.
 */
const EntryDatesDialog = ({ open, title, hint, start, end, onStart, onEnd, onClose, onSave, saveLabel, working }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ErpDateField label={t('erp.quotations.form.entryDate')} value={start}
            onChange={(value) => { onStart(value); onEnd(adjustEndDate(value, end, start)); }} />
          <ErpDateField label={t('erp.quotations.form.periodUntil')} value={end} onChange={onEnd}
            disabled={!start} minDate={start || undefined}
            helperText={t('erp.quotations.form.periodUntilHint')} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
        <Button variant="contained" disabled={working || !isValidQuotationPeriod(start, end)} onClick={onSave}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Detalle de cotización: datos + snapshot comercial congelado + estructura
 * de renta + simulación (o pendiente de motor) + acciones (simular,
 * duplicar, archivar, documento).
 *
 * Fases 2 y 3 del cotizador OUT/EST (2026-10-13): Editar (deshabilitado con
 * el motivo si tiene contrato o está archivada; en una subcotización solo
 * cambia la fecha de ingreso), mensual + proporcional lado a lado y "Personas
 * del cargo": la madre y sus subcotizaciones (COT-0009-1, -2…) con quién las
 * usa, "Agregar persona" y "Actualizar desde la madre".
 */
const ErpQuotationDetailLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quotationId } = useParams();
  const location = useLocation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [quotation, setQuotation] = useState(null);
  // Familia (madre + subcotizaciones con sus contratos); null si no cargó
  const [family, setFamily] = useState(null);
  const [familyError, setFamilyError] = useState(false);
  // Agregar persona / cambiar fecha de ingreso de una sub
  const [addOpen, setAddOpen] = useState(false);
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryStart, setEntryStart] = useState('');
  const [entryEnd, setEntryEnd] = useState('');
  const [entryAutoOpened, setEntryAutoOpened] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [dupStart, setDupStart] = useState('');
  const [dupEnd, setDupEnd] = useState('');

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await api.get(`/erp/quotations/${quotationId}`, {
        params: { organizationId: organization },
        ...headers,
      });
      if (response.data.response?.status_response === 'OK') {
        setQuotation(response.data.response.data);
      } else {
        setError(t('erp.quotations.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.quotations.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadFamily = async () => {
    try {
      const response = await api.get(`/erp/quotations/${quotationId}/family`, {
        params: { organizationId: organization },
        ...headers,
      });
      setFamily(response.data.response?.data || null);
      setFamilyError(false);
    } catch (err) {
      console.error(err);
      setFamily(null);
      setFamilyError(true);
    }
  };

  useEffect(() => {
    if (organization && quotationId) {
      setEntryAutoOpened(false);
      load();
      loadFamily();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, quotationId]);

  // Desde el listado o el wizard, "Editar" en una subcotización llega aquí
  // con openEntryDialog: se abre el cambio de fecha de ingreso
  useEffect(() => {
    if (entryAutoOpened || !quotation || !family || quotation.uid !== quotationId) return;
    setEntryAutoOpened(true);
    if (!location.state?.openEntryDialog || !quotation.parentQuotationId) return;
    const self = [family.root, ...(family.subQuotations || [])].find((m) => m.uid === quotation.uid);
    if (quotationEditBlock(quotation, self?.contracts)) return;
    setEntryStart(quotation.period?.startDate || '');
    setEntryEnd(quotation.period?.endDate || '');
    setEntryOpen(true);
    navigate(location.pathname, { replace: true, state: null }); // no reabrir al recargar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation, family]);

  const handleSimulate = async () => {
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/quotations/${quotationId}/simulate`,
        { organizationId: organization },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setQuotation(response.data.response.data);
        showAlert(t('erp.quotations.simulationOk'), 'success');
        loadFamily();
      }
    } catch (err) {
      const code = err.response?.data?.response?.code;
      if (SETUP_CODES.includes(code)) {
        showAlert(t('erp.quotations.parameterSetMissing'), 'warning');
      } else if (code === MINIMUM_WAGE_CODE) {
        // Fase 1b: sueldo base bajo el ingreso mínimo del mes de ingreso. Qué
        // hacer depende de la cotización: una sub se corrige en la madre; una
        // con contrato, duplicándola; si no, editándola
        let fix = 'fixInDetail';
        if (quotation?.parentQuotationId) fix = 'fixInParent';
        else if (quotation?.status === 'in_use') fix = 'fixByDuplicate';
        showAlert(`${minimumWageMessage(t, err.response.data.response)} ${t(`erp.quotations.minimumWage.${fix}`)}`, 'error');
      } else {
        console.error(err);
        showAlert(t('erp.quotations.simulationError'), 'error');
      }
    } finally {
      setWorking(false);
    }
  };

  // Descarga del documento PDF o del Excel de la cotización (fase 5,
  // 2026-10-13): mismo contenido — mes completo y proporcional lado a lado
  const downloadOutput = async (kind) => {
    const isExcel = kind === 'excel';
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/quotations/${quotationId}/${isExcel ? 'excel' : 'document'}`,
        { organizationId: organization },
        { ...headers, responseType: 'blob' }
      );
      const type = isExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `cotizacion_${quotation?.quotationNumber || quotationId}.${isExcel ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showAlert(t(isExcel ? 'erp.quotations.excelError' : 'erp.quotations.documentError'), 'error');
    } finally {
      setWorking(false);
    }
  };
  const handleDocument = () => downloadOutput('document');

  const handleDuplicate = async () => {
    if (!dupStart || !dupEnd) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }
    // Término >= inicio; desde el 2026-10-31 puede abarcar varios meses
    if (!isValidQuotationPeriod(dupStart, dupEnd)) {
      showAlert(t('erp.quotations.periodInvalid'), 'warning');
      return;
    }
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/quotations/${quotationId}/duplicate`,
        { organizationId: organization, startDate: dupStart, endDate: dupEnd },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        // Fase 1b: con los parámetros del nuevo mes el sueldo base puede quedar
        // bajo el ingreso mínimo; la copia se crea sin simular para editarla
        const warning = response.data.response.warning;
        if (warning?.code === MINIMUM_WAGE_CODE) {
          showAlert(t('erp.quotations.duplicateNoSimulation', { reason: minimumWageMessage(t, warning) }), 'warning');
        } else {
          showAlert(t('erp.quotations.duplicateSuccess'), 'success');
        }
        setDuplicateOpen(false);
        const uid = response.data.response.data?.uid;
        if (uid) navigate(`/erp/quotations/${uid}`);
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.quotations.saveError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleStatus = async (status) => {
    try {
      setWorking(true);
      const response = await api.patch(
        `/erp/quotations/${quotationId}/status`,
        { organizationId: organization, status },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setQuotation(response.data.response.data);
        loadFamily();
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      showAlert(message || t('erp.quotations.saveError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  // "Agregar persona": subcotización de la madre con su fecha de ingreso
  const handleAddPerson = async () => {
    if (!isValidQuotationPeriod(addStart, addEnd)) {
      showAlert(t('erp.quotations.periodInvalid'), 'warning');
      return;
    }
    try {
      setWorking(true);
      const rootId = family?.root?.uid || quotation.parentQuotationId || quotationId;
      const response = await api.post(
        `/erp/quotations/${rootId}/sub-quotations`,
        { organizationId: organization, startDate: addStart, endDate: addEnd },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        showAlert(
          t('erp.quotations.family.addSuccess', { number: response.data.response.data?.quotationNumber || '' }),
          'success'
        );
        setAddOpen(false);
        loadFamily();
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      let message = t('erp.quotations.family.addError');
      if (code === 'QUOTATION_ARCHIVED') message = t('erp.quotations.family.parentArchived');
      else if (code === MINIMUM_WAGE_CODE) {
        message = t('erp.quotations.family.addBelowMinimum', { reason: minimumWageMessage(t, err.response.data.response) });
      }
      showAlert(message, 'error');
    } finally {
      setWorking(false);
    }
  };

  // Subcotización: solo cambia su fecha de ingreso (re-simula en el backend)
  const handleEntryUpdate = async () => {
    if (!isValidQuotationPeriod(entryStart, entryEnd)) {
      showAlert(t('erp.quotations.periodInvalid'), 'warning');
      return;
    }
    try {
      setWorking(true);
      const response = await api.put(
        `/erp/quotations/${quotationId}`,
        { organizationId: organization, period: { startDate: entryStart, endDate: entryEnd } },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setQuotation(response.data.response.data);
        setEntryOpen(false);
        showAlert(t('erp.quotations.entryDialog.success'), 'success');
        loadFamily();
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      let message = t('erp.quotations.saveError');
      if (code === 'QUOTATION_HAS_CONTRACT') message = t('erp.quotations.editBlocked.contractNoName');
      else if (code === MINIMUM_WAGE_CODE) {
        message = t('erp.quotations.entryDialog.belowMinimum', { reason: minimumWageMessage(t, err.response.data.response) });
      }
      showAlert(message, 'error');
    } finally {
      setWorking(false);
    }
  };

  // "Actualizar desde la madre": subIds elegidas (todas si va vacío)
  const handleSync = async (subIds) => {
    if (!family?.root) return;
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/quotations/${family.root.uid}/sub-quotations/sync`,
        { organizationId: organization, subQuotationIds: subIds },
        headers
      );
      const result = response.data.response?.data || { updated: [], skipped: [] };
      // Fase 1b: las que con la estructura de la madre quedan bajo el ingreso
      // mínimo de su mes de ingreso no se actualizan
      const belowMinimum = (result.skipped || []).filter((s) => s.reason === MINIMUM_WAGE_CODE);
      if (belowMinimum.length > 0) {
        showAlert(
          t('erp.quotations.family.syncBelowMinimum', {
            list: belowMinimum.map((s) => s.quotationNumber).join(', '),
            count: result.updated.length,
          }),
          'warning'
        );
      } else if (result.updated.length > 0) {
        showAlert(
          t('erp.quotations.family.syncSuccess', { list: result.updated.map((u) => u.quotationNumber).join(', ') }),
          'success'
        );
      } else {
        showAlert(t('erp.quotations.family.syncNone'), 'info');
      }
      await Promise.all([load(true), loadFamily()]);
    } catch (err) {
      console.error(err);
      showAlert(t('erp.quotations.family.syncError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !quotation) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('erp.quotations.loadError')}</Alert>
      </Box>
    );
  }

  const snapshot = quotation.commercialSnapshot || {};
  const salary = quotation.salaryStructure || {};
  const marginLabel = snapshot.margin && snapshot.margin.value !== null && snapshot.margin.value !== undefined
    ? `${snapshot.margin.value}${snapshot.margin.feeType === 'percentage' ? '%' : ' $'}`
    : '—';
  // Fase 4 (2026-10-13): condiciones cambiadas en la cotización respecto del
  // acuerdo (snapshot.overrides) y las nuevas (margen mínimo, gastos
  // operacionales, provisiones). Una cotización anterior no las trae: su
  // margen mínimo y gastos operacionales salían de Parámetros.
  const overrides = snapshot.overrides || [];
  const withMark = (key, value) => (overrides.includes(key) ? (
    <>
      {value}
      <Chip component="span" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20 }}
        label={t('erp.quotations.conditions.modified')} />
    </>
  ) : value);
  const toggleText = (v) => (v?.enabled ? formatMoney(v.amount) : '—');
  const formulaProvisions = snapshot.provisions?.formula || {};
  const provisionItems = [
    ...FORMULA_PROVISIONS
      .filter((key) => formulaProvisions[key] === true)
      .map((key) => t(`erp.quotations.conditions.formula.${key}`)),
    ...(snapshot.provisions?.fixed || []).map((p) => `${p.name || t('erp.quotations.breakdown.agreementProvision')}: ${formatMoney(p.amount)}${
      p.prorated ? ` (${t('erp.quotations.detail.prorated')})` : ''}`),
  ];

  const listItems = (items) =>
    (items || []).length > 0
      ? items.map((i) => `${i.name}: ${formatMoney(i.amount)}`).join(' · ')
      : '—';

  // --- Edición y familia (fases 2 y 3) ---
  const isSub = Boolean(quotation.parentQuotationId);
  const root = family?.root || null;
  const subs = family?.subQuotations || [];
  const members = root ? [root, ...subs] : [];
  const self = members.find((m) => m.uid === quotation.uid);
  const editBlock = quotationEditBlock(quotation, self?.contracts);
  const editTooltip = editBlock
    ? t(`erp.quotations.editBlocked.${editBlock.key}`, { person: editBlock.person })
    : isSub ? t('erp.quotations.editSubHint') : '';
  const openEntryDialog = () => {
    setEntryStart(quotation.period?.startDate || '');
    setEntryEnd(quotation.period?.endDate || '');
    setEntryOpen(true);
  };
  const handleEdit = () => (isSub ? openEntryDialog() : navigate(`/erp/quotations/${quotationId}/edit`));

  const rootArchived = root?.status === 'archived';
  const memberHasContract = (m) => (m.contracts || []).length > 0 || m.status === 'in_use';
  // Motivo por el que una sub no se puede actualizar desde la madre (o null)
  const syncBlock = (m) => {
    if (memberHasContract(m)) {
      const person = contractPeople(m.contracts);
      return person
        ? t('erp.quotations.family.syncBlocked.contract', { person })
        : t('erp.quotations.family.syncBlocked.contractNoName');
    }
    if (m.status === 'archived') return t('erp.quotations.family.syncBlocked.archived');
    if (!m.outdated) return t('erp.quotations.family.syncBlocked.upToDate');
    return null;
  };
  const syncableSubs = subs.filter((m) => !syncBlock(m));
  const outdatedFree = subs.filter((m) => m.outdated && !memberHasContract(m) && EDITABLE_STATUSES.includes(m.status));

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/quotations')} sx={{ textTransform: 'none', mb: 1 }}>
        {t('erp.quotations.title')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {quotation.quotationNumber ? `${quotation.quotationNumber} · ` : ''}{quotation.clientName} — {quotation.jobTitleName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {quotation.costCenterName} · {t(`erp.quotations.serviceMode.${quotation.serviceMode}`)}
            {quotation.legalEntity?.name ? ` · ${quotation.legalEntity.name}` : ''} · {formatPeriod(quotation.period)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {isSub && (
            <Chip size="small" variant="outlined" color="secondary" sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/erp/quotations/${quotation.parentQuotationId}`)}
              label={t('erp.quotations.family.subOf', { number: quotation.parentQuotationNumber || '' })} />
          )}
          {quotation.duplicatedFromId && (
            <Chip size="small" variant="outlined" sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/erp/quotations/${quotation.duplicatedFromId}`)}
              label={`${t('erp.quotations.duplicatedFrom')} ${quotation.duplicatedFromNumber || ''}`.trim()} />
          )}
          <Chip label={t(`erp.quotations.status.${quotation.status}`)} color={STATUS_COLORS[quotation.status] || 'default'} />
          <Tooltip title={editTooltip}>
            <span>
              <Button variant="outlined" size="small" startIcon={<EditIcon />}
                disabled={working || Boolean(editBlock)} onClick={handleEdit} sx={{ textTransform: 'none' }}>
                {t('erp.quotations.edit')}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<CalculateIcon />} disabled={working}
            onClick={handleSimulate} sx={{ textTransform: 'none' }}>
            {t('erp.quotations.simulate')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<DuplicateIcon />} disabled={working}
            onClick={() => { setDupStart(''); setDupEnd(''); setDuplicateOpen(true); }} sx={{ textTransform: 'none' }}>
            {t('erp.quotations.duplicate')}
          </Button>
          {quotation.status === 'quoted' && (
            <Button variant="outlined" size="small" color="inherit" startIcon={<ArchiveIcon />} disabled={working}
              onClick={() => handleStatus('archived')} sx={{ textTransform: 'none' }}>
              {t('erp.quotations.archive')}
            </Button>
          )}
          {quotation.status === 'archived' && (
            <Button variant="outlined" size="small" startIcon={<UnarchiveIcon />} disabled={working}
              onClick={() => handleStatus('quoted')} sx={{ textTransform: 'none' }}>
              {t('erp.quotations.unarchive')}
            </Button>
          )}
          <Tooltip title={quotation.simulation ? '' : t('erp.quotations.documentNeedsSimulation')}>
            <span>
              <Button variant="outlined" size="small" startIcon={<DocumentIcon />}
                disabled={working || !quotation.simulation} onClick={handleDocument}
                sx={{ textTransform: 'none' }}>
                {t('erp.quotations.document')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={quotation.simulation ? '' : t('erp.quotations.excelNeedsSimulation')}>
            <span>
              <Button variant="outlined" size="small" startIcon={<ExcelIcon />}
                disabled={working || !quotation.simulation} onClick={() => downloadOutput('excel')}
                sx={{ textTransform: 'none' }}>
                {t('erp.quotations.excel')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Personas del cargo: madre + subcotizaciones (fase 3) */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t('erp.quotations.family.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {subs.length > 0 && (
              <Tooltip title={syncableSubs.length === 0 ? t('erp.quotations.family.syncAllBlocked') : ''}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<SyncIcon />}
                    disabled={working || syncableSubs.length === 0}
                    onClick={() => handleSync(syncableSubs.map((m) => m.uid))} sx={{ textTransform: 'none' }}>
                    {t('erp.quotations.family.syncAll')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title={rootArchived
              ? t('erp.quotations.family.parentArchived')
              : !root ? t('erp.quotations.family.loadError') : ''}>
              <span>
                <Button size="small" variant="outlined" startIcon={<AddPersonIcon />}
                  disabled={working || !root || rootArchived}
                  onClick={() => { setAddStart(''); setAddEnd(''); setAddOpen(true); }} sx={{ textTransform: 'none' }}>
                  {t('erp.quotations.family.add')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ my: 1 }} />
        {familyError && <Alert severity="warning" sx={{ mb: 1 }}>{t('erp.quotations.family.loadError')}</Alert>}
        {outdatedFree.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t('erp.quotations.family.outdatedAlert', { count: outdatedFree.length })}
          </Alert>
        )}
        {root && subs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: memberHasContract(root) ? 1 : 0 }}>
            {t('erp.quotations.family.hint', { number: root.quotationNumber || '' })}
          </Typography>
        )}
        {root && (subs.length > 0 || memberHasContract(root)) && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.quotations.family.number')}</TableCell>
                <TableCell>{t('erp.quotations.family.entryDate')}</TableCell>
                <TableCell>{t('erp.quotations.table.period')}</TableCell>
                <TableCell>{t('erp.common.status')}</TableCell>
                <TableCell>{t('erp.quotations.family.contract')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => {
                const isRoot = m.uid === root.uid;
                const isCurrent = m.uid === quotation.uid;
                const people = contractPeople(m.contracts);
                const blocked = isRoot ? null : syncBlock(m);
                return (
                  <TableRow key={m.uid} selected={isCurrent}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {isCurrent ? m.quotationNumber : (
                        <Link component="button" underline="hover" onClick={() => navigate(`/erp/quotations/${m.uid}`)}>
                          {m.quotationNumber}
                        </Link>
                      )}
                      {isRoot && <Chip size="small" label={t('erp.quotations.family.parent')} sx={{ ml: 1 }} />}
                      {isCurrent && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({t('erp.quotations.family.current')})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(m.period?.startDate)}</TableCell>
                    <TableCell>{formatPeriod(m.period)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`erp.quotations.status.${m.status}`)} color={STATUS_COLORS[m.status] || 'default'} />
                      {!isRoot && m.outdated && !memberHasContract(m) && EDITABLE_STATUSES.includes(m.status) && (
                        <Tooltip title={t('erp.quotations.family.outdatedTooltip')}>
                          <Chip size="small" color="warning" variant="outlined" sx={{ ml: 1 }}
                            label={t('erp.quotations.family.outdated')} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {people || (memberHasContract(m)
                        ? t('erp.quotations.family.withContract')
                        : t('erp.quotations.family.noContract'))}
                    </TableCell>
                    <TableCell align="right">
                      {!isRoot && (
                        <Tooltip title={blocked || ''}>
                          <span>
                            <Button size="small" startIcon={<SyncIcon />} disabled={working || Boolean(blocked)}
                              onClick={() => handleSync([m.uid])} sx={{ textTransform: 'none' }}>
                              {t('erp.quotations.family.sync')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('erp.quotations.detail.salaryTitle')}</Typography>
            <Divider sx={{ my: 1 }} />
            <Row label={t('erp.quotations.form.baseSalary')} value={formatMoney(salary.baseSalary)} />
            <Row label={t('erp.quotations.form.gratificationType')}
              value={salary.gratificationType ? t(`erp.quotations.gratification.${salary.gratificationType}`) : '—'} />
            <Row label={t('erp.quotations.form.mealAllowance')} value={formatMoney(salary.mealAllowance)} />
            <Row label={t('erp.quotations.form.transportAllowance')} value={formatMoney(salary.transportAllowance)} />
            <Row label={t('erp.quotations.form.teleworkAllowance')} value={formatMoney(salary.teleworkAllowance)} />
            <Row label={t('erp.quotations.form.taxableBonuses')} value={listItems(salary.taxableBonuses)} />
            <Row label={t('erp.quotations.form.otherAllowances')} value={listItems(salary.otherAllowances)} />
            <Row label={t('erp.quotations.form.additionalProvisions')} value={listItems(salary.additionalProvisions)} />
            <Row label={t('erp.quotations.form.vacationProvision')}
              value={salary.vacationProvision === false ? t('no') : t('yes')} />
            {/* El líquido objetivo solo se usa sin sueldo base (calculadora
                inversa); una cotización anterior podía traer los dos (2026-10-14) */}
            <Row label={t('erp.quotations.form.targetNetSalary')}
              value={formatMoney(salary.baseSalary ? null : salary.targetNetSalary)} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('erp.quotations.detail.snapshotTitle')}
              {snapshot.manual && (
                <Chip size="small" label={t('erp.quotations.inheritance.manualShort')} sx={{ ml: 1 }} />
              )}
              {!snapshot.manual && snapshot.agreementScope && (
                <Chip size="small" variant="outlined" sx={{ ml: 1 }}
                  label={snapshot.agreementScope === 'costCenter'
                    ? t('erp.quotations.detail.scopeCostCenter', {
                        costCenter: snapshot.agreementCostCenterName || quotation.costCenterName || '',
                      })
                    : t('erp.quotations.detail.scopeClient')} />
              )}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Row label={t('erp.agreement.margins.title')} value={withMark('margin', marginLabel)} />
            <Row label={t('erp.quotations.conditions.marginMinimum')}
              value={withMark('marginMinimum', snapshot.marginMinimum !== null && snapshot.marginMinimum !== undefined
                ? formatMoney(snapshot.marginMinimum)
                : t('erp.quotations.detail.fromParameters'))} />
            <Row label={t('erp.quotations.conditions.operationalExpenses')}
              value={withMark('operationalExpenses', snapshot.operationalExpenses === undefined
                ? t('erp.quotations.detail.fromParameters')
                : toggleText(snapshot.operationalExpenses))} />
            <Row label={t('erp.agreement.adminExpense')} value={withMark('adminExpense', toggleText(snapshot.adminExpense))} />
            <Row label={t('erp.agreement.assistanceCost')} value={withMark('assistanceCost', toggleText(snapshot.assistanceCost))} />
            <Row label={t('erp.quotations.detail.provisions')}
              value={withMark('provisions', provisionItems.length > 0 ? provisionItems.join(' · ') : '—')} />
            <Row label={t('erp.agreement.usesPurchaseOrder')}
              value={snapshot.usesPurchaseOrder ? t('yes') : t('no')} />
            <Row label={t('erp.clients.form.kam')} value={snapshot.kam?.name} />
            <Row label={t('erp.clients.form.sectionRequester')} value={snapshot.requester?.name} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('erp.quotations.detail.snapshotNote')}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('erp.quotations.detail.simulationTitle')}</Typography>
            <Divider sx={{ my: 1 }} />
            {quotation.simulation ? (
              <SimulationView simulation={quotation.simulation} period={quotation.period} />
            ) : (
              <Alert severity="info">{t('erp.quotations.noSimulation')}</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.quotations.duplicateTitle')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('erp.quotations.duplicateHint')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ErpDateField label={t('erp.quotations.form.entryDate')} value={dupStart}
              onChange={(value) => { setDupStart(value); setDupEnd((prev) => adjustEndDate(value, prev, dupStart)); }} />
            <ErpDateField label={t('erp.quotations.form.periodUntil')} value={dupEnd} onChange={setDupEnd}
              disabled={!dupStart} minDate={dupStart || undefined}
              helperText={t('erp.quotations.form.periodUntilHint')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" disabled={working} onClick={handleDuplicate}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {t('erp.quotations.duplicate')}
          </Button>
        </DialogActions>
      </Dialog>

      <EntryDatesDialog
        open={addOpen}
        title={t('erp.quotations.family.addTitle')}
        hint={t('erp.quotations.family.addHint', { number: root?.quotationNumber || quotation.quotationNumber || '' })}
        start={addStart} end={addEnd} onStart={setAddStart} onEnd={setAddEnd}
        onClose={() => setAddOpen(false)} onSave={handleAddPerson}
        saveLabel={t('erp.quotations.family.add')} working={working}
      />

      <EntryDatesDialog
        open={entryOpen}
        title={t('erp.quotations.entryDialog.title')}
        hint={t('erp.quotations.entryDialog.hint')}
        start={entryStart} end={entryEnd} onStart={setEntryStart} onEnd={setEntryEnd}
        onClose={() => setEntryOpen(false)} onSave={handleEntryUpdate}
        saveLabel={t('erp.common.save')} working={working}
      />
    </Box>
  );
};

export default ErpQuotationDetailLayout;
